import { Button, Empty, Popover } from 'antd'
import moment from 'moment'
import React, { memo, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { blue } from '../../color'
import {
  DEFAULT_END,
  DEFAULT_START,
  EXTRA_MINUTES,
  FULL_DAY_MINUTES,
  ITEM_GAP,
  ITEM_HEIGHT,
  RESOURCE_COLORS,
  ROW_PADDING_TOP,
  ceilHour,
  convertToOfficeCalendarResources,
  floorHour,
  formatTime,
  generateAxis,
  linkifyText,
  stackEvents,
  toMinutes,
} from './helper'

const WeekdayItem = memo(({ day, isSelected, onClick }) => {
  const [isHovered, setIsHovered] = React.useState(false)

  const isToday = moment().isSame(day, 'day')

  const getDayName = d => {
    const weekday = d.isoWeekday()
    switch (weekday) {
      case 1:
        return 'Thứ 2'
      case 2:
        return 'Thứ 3'
      case 3:
        return 'Thứ 4'
      case 4:
        return 'Thứ 5'
      case 5:
        return 'Thứ 6'
      case 6:
        return 'Thứ 7'
      case 7:
        return 'Chủ nhật'
      default:
        return ''
    }
  }

  const itemStyle = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    userSelect: 'none',
    opacity: isSelected || isHovered ? 1 : 0.8,
  }

  return (
    <div
      style={itemStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}>
      <div
        style={{
          fontSize: 14,
          fontWeight: isSelected ? 600 : 500,
          marginBottom: 4,
        }}>
        {getDayName(day)}
      </div>
      <div style={{ fontSize: 12 }}>{day.format('DD/MM')}</div>

      {isSelected && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: 4,
            background: blue,
          }}
        />
      )}
    </div>
  )
})

const LinkifiedText = memo(({ text }) => {
  const parts = linkifyText(text)

  return parts.map((part, idx) => {
    if (part.type === 'link') {
      return (
        <a
          key={idx}
          href={part.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}>
          {part.value}
        </a>
      )
    }
    return <React.Fragment key={idx}>{part.value}</React.Fragment>
  })
})

const OfficeCalendar = props => {
  const history = useHistory()

  const {
    data,
    selectedEventId,
    selectedDate,
    onEventClick,
    isShowPopoverContent = false,
    onChangeDate,
  } = props || {}

  const formatedCalendarResources = convertToOfficeCalendarResources(data)

  const weekDays = useMemo(() => {
    const startOfWeek = moment(selectedDate).startOf('isoWeek')
    const days = []
    for (let i = 0; i < 7; i++) {
      days.push(moment(startOfWeek).add(i, 'days'))
    }
    return days
  }, [selectedDate])

  const resourceColorsLength = RESOURCE_COLORS.length

  const {
    START_MIN,
    TOTAL_MINUTES,
    headerAxis,
    displayEndMin,
  } = useMemo(() => {
    const allEvents = formatedCalendarResources.flatMap(r => r.events)

    let start = DEFAULT_START
    let end = DEFAULT_END

    if (allEvents.length) {
      const minStart = Math.min(...allEvents.map(e => toMinutes(e.startTime)))
      const maxEnd = Math.max(...allEvents.map(e => toMinutes(e.endTime)))

      start = Math.min(floorHour(minStart), DEFAULT_START)
      end = Math.max(ceilHour(maxEnd), DEFAULT_END)
    }

    const shouldAddExtra = end < FULL_DAY_MINUTES
    const displayEnd = shouldAddExtra ? end + EXTRA_MINUTES : end

    return {
      START_MIN: start,
      TOTAL_MINUTES: displayEnd - start,
      headerAxis: generateAxis(start, end, 60),
      displayEndMin: displayEnd,
    }
  }, [formatedCalendarResources])

  const gridAxis = useMemo(() => generateAxis(START_MIN, displayEndMin, 30), [
    START_MIN,
    displayEndMin,
  ])

  const calculatePosition = (start, end) => {
    const startMin = toMinutes(start)
    const endMin = toMinutes(end)

    const left = ((startMin - START_MIN) / TOTAL_MINUTES) * 100
    const width = ((endMin - startMin) / TOTAL_MINUTES) * 100

    return { left, width }
  }

  const renderWeekdayDate = time => {
    if (!time) return 'Không rõ'
    const formatDay = moment(time).format('dddd')
    const formatDate = moment(time).format('DD')
    const formatMonth = moment(time).format('MM')
    return `${formatDay}, ${formatDate} Tháng ${formatMonth}`
  }

  return (
    <div style={{ width: '100%', fontSize: 14 }}>
      {/* WEEK DAYS BAR */}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 32,
          width: '100%',
          backgroundColor: '#DEF2FF',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }}>
        {weekDays.map(day => {
          const isSelected = moment(selectedDate).isSame(day, 'day')
          return (
            <WeekdayItem
              key={day.format('YYYY-MM-DD')}
              day={day}
              isSelected={isSelected}
              onClick={() => onChangeDate?.(day)}
            />
          )
        })}
      </div>

      <div style={{ width: '100%', overflowX: 'auto' }}>
        {/* HEADER */}
        <div
          style={{
            display: 'flex',
            height: 42,
            borderBottom: '1px solid #e5e7eb',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#DEF2FF',
          }}>
          <div
            style={{
              width: 200,
              padding: 12,
              fontWeight: 500,
              textTransform: 'capitalize',
              marginTop: -4,
            }}>
            {renderWeekdayDate(selectedDate)}
          </div>

          <div style={{ position: 'relative', flex: 1 }}>
            {headerAxis.map(t => {
              const left = ((t - START_MIN) / TOTAL_MINUTES) * 100

              return (
                <div
                  key={t}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    left: `${left}%`,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#121926',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}>
                  {formatTime(t)}
                </div>
              )
            })}
          </div>
        </div>
        {formatedCalendarResources.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={'Không có dữ liệu'}
            style={{ marginTop: 50 }}
          />
        ) : null}

        {formatedCalendarResources.map((resource, index) => {
          const stackedRows = stackEvents(resource.events)

          const dynamicRowHeight =
            Math.max(1, stackedRows.length) * (ITEM_HEIGHT + ITEM_GAP) +
            ROW_PADDING_TOP

          return (
            <div
              key={resource.id}
              style={{
                display: 'flex',
                borderBottom: '1px solid #e5e7eb',
                transition: 'all 0.2s',
              }}>
              {/* RESOURCE NAME */}
              <div
                style={{
                  width: 200,
                  padding: 12,
                  fontWeight: 500,
                  borderRight: '1px solid #e5e7eb',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <div
                  style={{
                    overflow: 'hidden',
                    width: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}>
                  <LinkifiedText text={resource.name} />
                </div>
              </div>

              {/* TIMELINE */}
              <div
                style={{
                  position: 'relative',
                  flex: 1,
                  minHeight: dynamicRowHeight,
                }}>
                {/* GRID */}
                {gridAxis.slice(0, -1).map(t => {
                  const left = ((t - START_MIN) / TOTAL_MINUTES) * 100
                  const isFullHour = t % 60 === 0
                  if (left >= 100) return null

                  return (
                    <div
                      key={t}
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        width: 1,
                        left: `${left}%`,
                        zIndex: 0,
                        background: isFullHour ? '#e5e7eb' : 'transparent',
                        borderLeft: isFullHour
                          ? undefined
                          : '1px dashed #e5e7eb',
                      }}
                    />
                  )
                })}

                {/* LUNCH */}
                {(() => {
                  const lunchStart = toMinutes('12:00')
                  const lunchEnd = toMinutes('13:30')

                  if (lunchEnd <= START_MIN || lunchStart >= displayEndMin)
                    return null

                  const left = Math.max(
                    0,
                    ((lunchStart - START_MIN) / TOTAL_MINUTES) * 100
                  )

                  const width = Math.min(
                    100 - left,
                    ((lunchEnd - lunchStart) / TOTAL_MINUTES) * 100
                  )

                  return (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${left}%`,
                        width: `${width}%`,
                        background: 'rgba(229,231,235,0.5)',
                        zIndex: 10,
                        pointerEvents: 'none',
                      }}
                    />
                  )
                })()}

                {/* EVENTS */}
                {stackedRows.map((rowEvents, rowIndex) =>
                  rowEvents.map(event => {
                    const { left, width } = calculatePosition(
                      event.startTime,
                      event.endTime
                    )

                    const isSelected = selectedEventId === event.id
                    const resourceColor =
                      RESOURCE_COLORS[index % resourceColorsLength]

                    return (
                      <Popover
                        overlayClassName="custom-office-calendar-popover"
                        key={event.id}
                        open={isSelected && isShowPopoverContent}
                        placement="rightBottom"
                        align={{ offset: [-30, -25] }}
                        trigger="click"
                        onOpenChange={open => !open && onEventClick?.(null)}
                        {...(isShowPopoverContent && {
                          content: (
                            <div style={{ minWidth: 200, maxWidth: 350 }}>
                              <div style={{ fontSize: 12 }}>
                                <span>Nội dung</span>
                                <p
                                  style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    width: '100%',
                                    marginBottom: 4,
                                    fontSize: 14,
                                  }}
                                  title={event.title}>
                                  {event.title}
                                </p>
                              </div>
                              <div style={{ fontSize: 12 }}>
                                <span>Thời gian</span>
                                <p
                                  style={{
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    width: '100%',
                                    marginBottom: 4,
                                  }}>
                                  {event.startTime} - {event.endTime},{' '}
                                  {event.date}
                                </p>
                              </div>

                              <div style={{ fontSize: 12 }}>
                                <span>Địa điểm</span>
                                <p
                                  style={{
                                    width: '100%',
                                    marginBottom: 4,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                  }}>
                                  <LinkifiedText text={event.location} />
                                </p>
                              </div>

                              <div style={{ fontSize: 12 }}>
                                <span>Chuẩn bị</span>
                                <p
                                  style={{
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    width: '100%',
                                    marginBottom: 4,
                                  }}
                                  title={event.preparation}>
                                  {event.preparation}
                                </p>
                              </div>

                              <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={() =>
                                  history.push(
                                    `/company-work-schedule/view/${event.id}`
                                  )
                                }>
                                Xem chi tiết
                              </Button>
                            </div>
                          ),
                        })}>
                        <div
                          onClick={() => onEventClick?.(event)}
                          style={{
                            position: 'absolute',
                            zIndex: 20,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            borderRadius: 8,
                            padding: '6px',
                            fontSize: 12,
                            boxShadow: isSelected
                              ? '0 4px 10px rgba(0,0,0,0.15)'
                              : '0 2px 6px rgba(0,0,0,0.1)',
                            border: isSelected ? '2px solid #1677ff' : 'none',
                            left: `${left}%`,
                            width: `${width}%`,
                            top:
                              rowIndex * (ITEM_HEIGHT + ITEM_GAP) +
                              ROW_PADDING_TOP,
                            height: ITEM_HEIGHT,
                            background: resourceColor,
                          }}>
                          <div style={{ fontWeight: 500, overflow: 'hidden' }}>
                            {event.title}
                          </div>
                          <div style={{ fontSize: 11, opacity: 0.7 }}>
                            {event.host}
                          </div>
                        </div>
                      </Popover>
                    )
                  })
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default memo(OfficeCalendar)
